import { useState, useEffect, useRef } from 'react';
import { FaTelegram } from 'react-icons/fa';
import { HiDownload, HiArrowRight, HiPlus } from 'react-icons/hi';
import { HiMagnifyingGlass } from 'react-icons/hi2';
import logoPng from './assets/logo_png.png';
import logoJpeg from './assets/logo.jpeg';
import logoLandscape from './assets/landscape_logo.png';
import HeroParticles from './components/HeroParticles';
import SocialsSection from './components/SocialsSection';
import './App.css';

const TELEGRAM_CHANNEL = 'VGTO_ClubGG';
const POSTS_TO_SHOW = 4;
const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

// Fetches the latest post IDs from a public Telegram channel
function useTelegramPosts(channel, count) {
  const [postIds, setPostIds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchPosts = async () => {
      try {
        const res = await fetch(
          `https://api.allorigins.win/raw?url=${encodeURIComponent(`https://t.me/s/${channel}`)}`
        );
        const html = await res.text();
        // Extract post IDs from data-post attributes (format: "CHANNEL/ID")
        const matches = [...html.matchAll(/data-post="[^/]+\/(\d+)"/g)];
        const ids = matches.map((m) => parseInt(m[1], 10));
        // Dedupe, remove service messages (post 1), sort descending, take latest N
        const unique = [...new Set(ids)]
          .filter((id) => id > 1)
          .sort((a, b) => b - a)
          .slice(0, count);
        if (mounted && unique.length > 0) {
          setPostIds(unique);
        }
      } catch (err) {
        console.warn('Failed to fetch Telegram posts:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchPosts();
    const interval = setInterval(fetchPosts, POLL_INTERVAL);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [channel, count]);

  return { postIds, loading };
}

// Renders a single Telegram post widget — lets the widget handle its own sizing
function TelegramPost({ channel, postId }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.innerHTML = '';
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-post', `${channel}/${postId}`);
    script.setAttribute('data-width', '100%');
    script.setAttribute('data-dark', '1');
    script.setAttribute('data-color', 'FF2C3B');
    script.setAttribute('data-userpic', 'true');
    script.async = true;
    containerRef.current.appendChild(script);
  }, [channel, postId]);

  return <div ref={containerRef} className="telegram-post-widget" />;
}

// Telegram feed section component
function TelegramFeed() {
  const { postIds, loading } = useTelegramPosts(TELEGRAM_CHANNEL, POSTS_TO_SHOW);

  return (
    <div className="updates-card">
      <a
        href={`https://t.me/${TELEGRAM_CHANNEL}`}
        target="_blank"
        rel="noopener noreferrer"
        className="updates-channel-header"
      >
        <div className="updates-tg-icon">
          <FaTelegram />
        </div>
        <div>
          <div className="updates-channel-name">ClubGTO - ClubGG</div>
          <div className="updates-channel-handle">@{TELEGRAM_CHANNEL}</div>
        </div>
      </a>
      <div className="updates-scroll">
        {loading && postIds.length === 0 && (
          <div className="updates-loading">Loading updates...</div>
        )}
        {postIds.map((id) => (
          <TelegramPost key={id} channel={TELEGRAM_CHANNEL} postId={id} />
        ))}
      </div>
      <div className="updates-feed-footer">
        <a
          href={`https://t.me/${TELEGRAM_CHANNEL}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary updates-cta"
        >
          <FaTelegram />
          Join Channel for More
        </a>
      </div>
    </div>
  );
}

// redeploy
function App() {
  const [scrolled, setScrolled] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const registerRef = (id) => (el) => {
    sectionRefs.current[id] = el;
  };

  return (
    <div className="landing-page">
      {/* Background Effects */}
      <div className="bg-gradient-top" />
      <div className="bg-gradient-bottom" />

      {/* Navbar */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="nav-logo">
            <img src={logoJpeg} alt="ClubGTO" className="nav-logo-img" />
          </div>
          <a href="#steps" className="nav-cta">Get Started</a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero" id="hero" ref={registerRef('hero')}>
        <HeroParticles />
        <div className="hero-content">
          <div className="animate-in">
            <div className="hero-badge">
              <span className="hero-badge-dot" />
              Live on ClubGG
            </div>
          </div>

          <h1 className="animate-in delay-1">
            PLAY ONLINE POKER<br />
            <span className="highlight">WITH ME </span>
            AT <img src={logoLandscape} alt="ClubGTO" className="hero-inline-logo" />
          </h1>

          <p className="hero-subtitle animate-in delay-2">
            Follow the steps below to get started and join
            the ClubGTO community on Club GG!
          </p>

          <div className="hero-buttons animate-in delay-3">
            <a href="#steps" className="btn-primary">
              Get Started
              <HiArrowRight />
            </a>
            <a href="#socials" className="btn-secondary">
              Follow Me
            </a>
            <a
              href="https://t.me/m/nfi_jVG0N2Vh"
              target="_blank"
              rel="noopener noreferrer"
              className="hero-dm-link"
            >
              <FaTelegram />
              or Message Us directly — we&rsquo;ll help you get started
            </a>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section
        className="steps-section"
        id="steps"
        ref={registerRef('steps')}
      >
        <div className="container">
          <div className={`section-header ${visibleSections.steps ? 'animate-in' : ''}`}>
            <div className="section-label">How to Join</div>
            <h2 className="section-title">Get Started in 3 Easy Steps</h2>
            <p className="section-subtitle">
              Join our thriving poker community in just a few minutes.
            </p>
          </div>

          <div className="steps-grid">
            {/* Step 1 */}
            <div className={`step-card ${visibleSections.steps ? 'animate-in delay-1' : ''}`}>
              <div className="step-number">1</div>
              <h3>Download the ClubGG App</h3>
              <p>
                If you are a new player, download the ClubGG Poker app on the
                App Store or Google Play to get started. You can download it from their website or by just hitting the button below!
              </p>
              <a
                href="https://apps.apple.com/us/app/clubgg-poker/id1529839330"
                target="_blank"
                rel="noopener noreferrer"
                className="step-cta"
              >
                <HiDownload />
                Download
              </a>
            </div>

            {/* Step 2 */}
            <div className={`step-card ${visibleSections.steps ? 'animate-in delay-2' : ''}`}>
              <div className="step-number">2</div>
              <h3>Join the Club with Code</h3>
              <p>
                Hit the button below or the &ldquo;Search Club&rdquo; button in the app and enter the
                Club ID below, hit Search, then Join, You&rsquo;re in!
              </p>
              <div className="club-code">790203</div>
              <br />
              <a href="https://clubgg.app.link/v1I62IY1yVb" className="step-cta">
                <HiMagnifyingGlass />
                Join the Club
              </a>
            </div>

            {/* Step 3 */}
            <div className={`step-card ${visibleSections.steps ? 'animate-in delay-3' : ''}`}>
              <div className="step-number">3</div>
              <h3>Message the 24/7 Live Support Team!</h3>
              <p>
                Hit the button below or message our 24/7 live dedicated support team on Telegram{' '}
                <strong style={{ color: 'var(--accent)' }}>@ClubGTO</strong> and
                get an INSTANT 5% First Deposit Bonus! You will receive an
                instant response to any requests or questions.
              </p>
              <a
                href="https://t.me/m/nfi_jVG0N2Vh"
                target="_blank"
                rel="noopener noreferrer"
                className="step-cta"
              >
                <FaTelegram />
                Message
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Updates Section */}
      <section
        className="updates-section"
        id="updates"
        ref={registerRef('updates')}
      >
        <div className="container">
          <div className={`section-header ${visibleSections.updates ? 'animate-in' : ''}`}>
            <div className="section-label">Latest Updates</div>
            <h2 className="section-title">Stay in the Loop!</h2>
            <p className="section-subtitle">
              Live updates straight from our Telegram channel. Follow to never miss a game.
            </p>
          </div>

          <div className={`updates-wrapper ${visibleSections.updates ? 'animate-in delay-1' : ''}`}>
            <TelegramFeed />
          </div>
        </div>
      </section>

      <SocialsSection
        visible={visibleSections.socials}
        sectionRef={registerRef('socials')}
      />

      {/* FAQ Section */}
      <section
        className="faq-section"
        id="faq"
        ref={registerRef('faq')}
      >
        <div className="container">
          <div className={`section-header ${visibleSections.faq ? 'animate-in' : ''}`}>
            <div className="section-label">FAQ</div>
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">
              Everything you need to know about deposits and cashouts.
            </p>
          </div>

          <div className="faq-grid">
            {/* FAQ 1 */}
            <div className={`faq-item ${activeFaq === 0 ? 'active' : ''} ${visibleSections.faq ? 'animate-in delay-1' : ''}`}>
              <button className="faq-question" onClick={() => toggleFaq(0)}>
                <span>What methods are accepted for deposits?</span>
                <span className="faq-icon"><HiPlus /></span>
              </button>
              <div className="faq-answer">
                <div className="faq-answer-content">
                  <ul>
                    <li>Crypto</li>
                    <li>Debit Card</li>
                    <li>Apple Pay</li>
                    <li>Zelle</li>
                    <li>Venmo</li>
                    <li>Cashapp</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* FAQ 2 */}
            <div className={`faq-item ${activeFaq === 1 ? 'active' : ''} ${visibleSections.faq ? 'animate-in delay-2' : ''}`}>
              <button className="faq-question" onClick={() => toggleFaq(1)}>
                <span>How are cashouts processed?</span>
                <span className="faq-icon"><HiPlus /></span>
              </button>
              <div className="faq-answer">
                <div className="faq-answer-content">
                  <ol>
                    <li>Let us know your Club GG ID (8-digit number)</li>
                    <li>The amount you wish to cash out</li>
                    <li>The method you would like to cash out (Venmo, Zelle, Cashapp, and Crypto)</li>
                    <li>The address to the cashout method</li>
                    <li><strong>Your cashout will be processed instantly!</strong></li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>





      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-brand">
            <img src={logoPng} alt="ClubGTO" className="footer-logo" />
          </div>
          <div className="footer-text">
            &copy; {new Date().getFullYear()} ClubGTO. All rights reserved.
          </div>
        </div>
      </footer>

    </div>
  );
}

export default App;